/**
 * Supabase environment access.
 *
 * The publishable (anon) key + URL are safe in the browser; the secret
 * (service-role) key is server-only and must never reach the client bundle.
 * Reads are lazy (functions, not module constants) so that importing this file
 * never throws — the app boots even when Supabase vars are absent, and only
 * the auth surfaces that actually need Supabase fail, loudly, at call time.
 */

/** URL + publishable key for browser/SSR clients, or null when unconfigured. */
export function getPublicSupabaseEnv(): {
  url: string;
  publishableKey: string;
} | null {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const publishableKey = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !publishableKey) return null;
  return { url, publishableKey };
}

/** True when the browser/SSR Supabase client can be constructed. */
export function isSupabaseConfigured(): boolean {
  return getPublicSupabaseEnv() !== null;
}

/**
 * Server-only secret key for the AUTH project — the one NEXT_PUBLIC_SUPABASE_URL
 * points at. Null when unconfigured.
 *
 * The `SUPABASE_SERVICE_ROLE_KEY` fallback is not cosmetic. Production sets that
 * name and NOT `SUPABASE_SECRET_KEY`, so this returned null on the live box
 * (verified against /proc/<pid>/environ, not just the env file). Every caller
 * guards with `if (secret)`, so nothing threw — account deletion simply skipped
 * removing the Supabase auth user while still deleting the profile rows, which
 * is a GDPR erasure that half-happens and says nothing. This file's own header
 * promises the opposite: "only the auth surfaces that actually need Supabase
 * fail, loudly, at call time."
 */
export function getSupabaseSecretKey(): string | null {
  return process.env.SUPABASE_SECRET_KEY ?? process.env.SUPABASE_SERVICE_ROLE_KEY ?? null;
}

// Portfolio image storage is no longer Supabase at all: see src/lib/media.ts (CZ-591).

/** Throwing accessor for code paths that genuinely require Supabase. */
export function requirePublicSupabaseEnv(): {
  url: string;
  publishableKey: string;
} {
  const env = getPublicSupabaseEnv();
  if (!env) {
    throw new Error(
      "Supabase is not configured: set NEXT_PUBLIC_SUPABASE_URL and " +
        "NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY.",
    );
  }
  return env;
}
